import { Component, DestroyRef, OnInit, OnChanges, SimpleChanges, Input, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs';
import type { EChartsOption } from 'echarts';
import { MappingService } from '../../services/mapping.service';
import { FieldDashboardDataService } from '../field-dashboard-data.service';
import { isoDate, seasonFromLabel } from '../macronutrients/season-period';
import { NDVI_QUICK_RANGES, NdviStats, ndviChartOptions, ndviRange, ndviStats } from '../../shared/ndvi-trend';

export interface NdviPeriod {
  from: string;
  to: string;
}

/**
 * The page's period, as the other Field Analysis charts read it: the picked start and end dates once
 * the user has changed them, else the season dropdown's 1 June to 31 May. Null until there is one.
 */
export function pagePeriod(data: any): NdviPeriod | null {
  if (data?.userChangedDates && data.startDate && data.endDate) {
    return { from: isoDate(data.startDate), to: isoDate(data.endDate) };
  }
  const season = seasonFromLabel(data?.season);
  return season ? { from: season.from, to: season.to } : null;
}

/**
 * Field Analysis's NDVI Index chart (Heinrich, 28/10/2026). It follows the page's period, and follows it
 * again whenever the season or the dates at the top change. 1M / 3M / 6M / All override that until the
 * period at the top changes or Period is pressed; the same buttons as the maps field panel's NDVI card,
 * with Min / Avg / Max for what is shown and a timeline slider on All.
 */
@Component({
  selector: 'app-ndvi-chart',
  templateUrl: './ndvi-chart.component.html',
  styleUrls: ['./ndvi-chart.component.css'],
  standalone: false
})
export class NdviChartComponent implements OnInit, OnChanges {
  @Input() fieldId: string = '';

  readonly quickRanges = NDVI_QUICK_RANGES;
  /** Months for a quick period, 0 for All, null while it follows the page's period. */
  activeRange: number | null = null;
  /** The page's period, once known. */
  page: NdviPeriod | null = null;
  fromDate = ndviRange(6).from;
  toDate = ndviRange(6).to;

  chartOption: EChartsOption = {};
  loading = false;
  failed = false;
  hasData = false;
  stats: NdviStats | null = null;

  private chartInstance: any;
  /** The latest request; an older answer that lands after it is dropped. */
  private requestSeq = 0;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private mappingService: MappingService,
    private dataService: FieldDashboardDataService
  ) {}

  ngOnInit(): void {
    this.dataService.fieldData$
      .pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
      .subscribe(data => this.onPageData(data));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fieldId'] && !changes['fieldId'].firstChange && this.fieldId) {
      this.loadNdviData();
    }
  }

  /**
   * A change of the period at the top brings the chart back to it, override or not. Updates that leave
   * the period as it was (the crop, the links loading) leave an override alone.
   */
  onPageData(data: any): void {
    const period = pagePeriod(data);
    if (!period || (this.page && this.page.from === period.from && this.page.to === period.to)) {
      return;
    }
    this.page = period;
    this.followPage();
  }

  /** Back to the page's period. */
  followPage(): void {
    if (!this.page) return;
    this.activeRange = null;
    this.fromDate = this.page.from;
    this.toDate = this.page.to;
    this.loadNdviData();
  }

  /** A quick period in months, 0 for All, ending today. Holds until the period at the top changes. */
  setRange(months: number): void {
    this.activeRange = months;
    const range = ndviRange(months);
    this.fromDate = range.from;
    this.toDate = range.to;
    this.loadNdviData();
  }

  onChartInit(instance: any): void {
    this.chartInstance = instance;
  }

  exportChart(): void {
    if (!this.chartInstance) return;
    const link = document.createElement('a');
    link.href = this.chartInstance.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#ffffff' });
    link.download = `NDVI ${this.fromDate} to ${this.toDate}.png`;
    link.click();
  }

  loadNdviData(): void {
    if (!this.fieldId) return;
    const seq = ++this.requestSeq;
    this.loading = true;
    this.failed = false;
    this.mappingService.getNdviChart(Number(this.fieldId), this.fromDate, this.toDate).subscribe({
      next: (data) => {
        if (seq !== this.requestSeq) return;
        this.loading = false;
        this.hasData = !!data?.length;
        this.stats = this.hasData ? ndviStats(data) : null;
        this.chartOption = this.hasData
          ? ndviChartOptions(data, {
              slider: this.activeRange === 0,
              compact: false,
              colours: { min: '#663333', max: '#0000CC', mean: '#8dc63f' }
            })
          : {};
      },
      error: () => {
        if (seq !== this.requestSeq) return;
        this.loading = false;
        this.failed = true;
        this.hasData = false;
        this.stats = null;
      }
    });
  }
}
